import {UseFormSetValue} from 'react-hook-form';
import {FormValues} from '../schema';
import {
  IIngestionCopyResponse,
  IngestionCopyKeyValueItem,
} from '../../../../api/types/ingestionApi.type';
import {getIngestionTypeFromOverwriteValue} from './formPrefillUtils';

type DropdownOption = {key: string; displayValue: string};

/**
 * Helper to find selected dropdown option based on copy API's "selected" field logic
 *
 * Logic:
 * 1. Get the "selected" value from copy API (e.g., "-4")
 * 2. Find the copy API option where value === selected
 * 3. Match that option's key with wizard dropdown's displayValue
 */
const findSelectedOption = (
  apiOptions: IngestionCopyKeyValueItem[],
  wizardOptions: DropdownOption[]
): DropdownOption | null => {
  if (!apiOptions || apiOptions.length === 0) {
    return null;
  }

  // Get the "selected" value (all items have same selected value)
  const selectedValue = apiOptions[0]?.selected;
  if (!selectedValue) {
    return null;
  }

  // ✅ FIX: Compare as strings to handle type mismatches
  const selectedApiOption = apiOptions.find(
    (opt) => String(opt.value) === String(selectedValue)
  );

  if (!selectedApiOption) {
    return null;
  }

  // Match copy API's "key" with wizard dropdown's "displayValue"
  const matchedWizardOption = wizardOptions.find(
    (opt) => opt.displayValue === selectedApiOption.key
  );

  return matchedWizardOption || null;
};

export const prefillCopyFlowDropdowns = (
  loadedData: IIngestionCopyResponse,
  setValue: UseFormSetValue<FormValues>,
  wizardDropdowns: {
    sourceSystem: DropdownOption[];
    serverLocation: DropdownOption[];
    columnDelimeter: DropdownOption[];
    dataDelimeter: DropdownOption[];
    textQualifier: DropdownOption[];
    dateFormat: DropdownOption[];
  }
) => {
  const settings = loadedData.ingestionWizardModel.ingestionSpecifySetting;
  const apiDropdowns = loadedData.fillDropDownListFromSessionResult.result;

  // Set Ingestion Type
  const ingestionTypeOption = apiDropdowns.OverwriteOption?.find(
    (opt: any) => opt.value === settings.overwriteValue
  );
  if (ingestionTypeOption) {
    setValue('ingestionType', {
      key: ingestionTypeOption?.value,
      displayValue: ingestionTypeOption?.key,
    });
  } else {
    const overwriteValue = settings?.overwriteValue || loadedData.overwriteValue;
    const fallbackIngestionType = getIngestionTypeFromOverwriteValue(overwriteValue);
    if (fallbackIngestionType) {
      setValue('ingestionType', fallbackIngestionType, {shouldDirty: false});
    }
  }
  // 1. Source System
  if (apiDropdowns.SourceSystem) {
    const selectedSourceSystem = findSelectedOption(
      apiDropdowns.SourceSystem,
      wizardDropdowns.sourceSystem
    );
    if (selectedSourceSystem) {
      setValue('sourceSystem', selectedSourceSystem, {shouldDirty: false});
    }
  }

  // 2. Server Location
  if (apiDropdowns.ServerLocation) {
    const selectedServerLocation = findSelectedOption(
      apiDropdowns.ServerLocation,
      wizardDropdowns.serverLocation
    );
    if (selectedServerLocation) {
      setValue('serverLocation', selectedServerLocation, {shouldDirty: false});
    }
  }

  // 3. Field Separator (Column Delimiter)
  if (apiDropdowns.ColumnDelimiter) {
    const selectedFieldSeparator = findSelectedOption(
      apiDropdowns.ColumnDelimiter,
      wizardDropdowns.columnDelimeter
    );
    if (selectedFieldSeparator) {
      setValue('fieldSeparator', selectedFieldSeparator, {shouldDirty: false});
    }
  }

  // 4. Text Qualifier (Text Qualifier)
  if (apiDropdowns.TextQualifier) {
    const selectedTextQualifier = findSelectedOption(
      apiDropdowns.TextQualifier,
      wizardDropdowns.textQualifier
    );
    if (selectedTextQualifier) {
      setValue('textQualifier', selectedTextQualifier, {shouldDirty: false});
    }
  }

  // 5. Multi-Value (Data Delimiter)
  if (apiDropdowns.DataDelimiter) {
    const selectedMultiValue = findSelectedOption(
      apiDropdowns.DataDelimiter,
      wizardDropdowns.dataDelimeter
    );
    if (selectedMultiValue) {
      setValue('multiValue', selectedMultiValue, {shouldDirty: false});
    }
  }

  // 6.  Date Format
  if (apiDropdowns.DateFormat) {
    const selectedDateFormat = findSelectedOption(
      apiDropdowns.DateFormat,
      wizardDropdowns.dateFormat
    );
    if (selectedDateFormat) {
      setValue('dateFormat', selectedDateFormat, {shouldDirty: false});
    }
  }
};
